import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useForm, Controller } from 'react-hook-form'
import { useMutation } from 'react-apollo'

import Form from '../forms/layout-form'
import Row from '../forms/layout-row'
import Col from '../forms/layout-column'
import PinInput from '../forms/pin-input'
import Button from '../button'
import { FORM_VALUES, FORM_MESSAGES, PIN_RULES } from '../../constants'
import { getGQLErrorMsg } from '../../lib/apollo'
import ACCOUNT_MUTATION from '../../../graphql/mutations/account.gql'
import { useMutateAndLoading } from '../../hooks/use-mutate-and-loading'

/*
 * Resets the user's PIN in this form.
 * If successful, navigates to the PIN reset success modal.
 */
function PinResetForm({
  onPinReset
}) {
  const [errorMessage, setErrorMessage] = useState('')
  const [resetPinMutation] = useMutation(ACCOUNT_MUTATION)
  const mutateAndLoading = useMutateAndLoading(resetPinMutation)

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: {
      errors, touchedFields, isSubmitted, isSubmitting
    }
  } = useForm({
    defaultValues: {
      pin: '',
      pinConfirmation: ''
    },
    mode: 'onChange'
  })

  const onSubmit = async values => {
    setErrorMessage('')

    try {
      const data = await mutateAndLoading('pinSetup', {
        variables: {
          input: {
            pin: values.pin
          }
        }
      })

      if (data?.account?.hasPin) {
        onPinReset()
        reset()
      }
    } catch (error) {
      setErrorMessage(getGQLErrorMsg(error))
    }
  }

  return (
    <Form isSubmitting={isSubmitting} handleSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col>
          <Controller
            name="pin"
            control={control}
            rules={PIN_RULES}
            render={({ field }) => (
              <PinInput
                label="PIN"
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                warning={
                  (field.value || touchedFields?.pin || isSubmitted) &&
                  errors.pin?.message
                }
                isValid={
                  !errorMessage &&
                  Boolean(
                    field.value && touchedFields?.pin && !errors.pin
                  )
                }
                maxLength={`${FORM_VALUES.pin.length}`}
              />
            )}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Controller
            name="pinConfirmation"
            control={control}
            rules={{
              ...PIN_RULES,
              validate: value => value === getValues('pin') || FORM_MESSAGES.pin.match
            }}
            render={({ field }) => (
              <PinInput
                label="Confirm PIN"
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                warning={
                  (field.value || touchedFields?.pinConfirmation || isSubmitted) &&
                  errors.pinConfirmation?.message
                }
                isValid={
                  !errorMessage &&
                  Boolean(
                    field.value &&
                      touchedFields?.pinConfirmation &&
                      !errors.pinConfirmation
                  )
                }
                maxLength={`${FORM_VALUES.pin.length}`}
              />
            )}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Button disabled={isSubmitting} text="RESET PIN" />
        </Col>
      </Row>
    </Form>
  )
}

PinResetForm.propTypes = {
  onPinReset: PropTypes.func.isRequired
}

export default PinResetForm
